import { type JSX, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { AppLink } from '../../discovery/router';
import { routes } from '../../routes';
import { LoginLink } from '../panels';
import {
  type AnswerValue,
  type Availability,
  type Booking,
  type OfferCard,
  type Question,
  type Requirement,
  type Slot,
  SessionApiError,
  confirmBooking,
  getOffer,
  getRequirement,
  getSlots,
  holdSlot,
  newKey,
  releaseHold,
  submitConsultation,
  uploadEvidence,
} from './store-api';
import { useLoad } from './store-hooks';
import {
  bookingIcs,
  dayKey,
  isShown,
  legalOf,
  money,
  monthGrid,
  priceLabel,
  savingBadge,
  sizeOf,
  slotGroups,
  timeOf,
  whenLabel,
} from './store-model';
import { Icon, PATHS } from './store-icons';

/**
 * The Store frame's four overlays — the product sheet, the pharmacy
 * medicine's "A few quick questions", the prescription medicine's
 * consultation, and "Book a service" — each driven by the platform's own
 * routes. Nothing in them is the frame's demo data: the questions are the
 * pharmacy's published questionnaire, the times are its diary, and every
 * answer, hold and booking is the server's.
 */

// -- The sheet ------------------------------------------------------------------

export function Sheet({
  label,
  labelledBy,
  onClose,
  wide = false,
  layer = 0,
  children,
}: {
  readonly label?: string;
  readonly labelledBy?: string;
  readonly onClose: () => void;
  readonly wide?: boolean;
  readonly layer?: number;
  readonly children: ReactNode;
}): JSX.Element {
  const dialog = useRef<HTMLDivElement>(null);
  const closer = useRef(onClose);
  closer.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closer.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      before?.focus?.();
    };
  }, []);

  return (
    <>
      <div className="jt-st-scrim" style={{ zIndex: 90 + layer * 2 }} onClick={onClose} aria-hidden="true" />
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        {...(label !== undefined ? { 'aria-label': label } : {})}
        {...(labelledBy !== undefined ? { 'aria-labelledby': labelledBy } : {})}
        tabIndex={-1}
        className={`jt-st-sheet${wide ? ' jt-st-sheet--wide' : ''}`}
        style={{ zIndex: 91 + layer * 2 }}
      >
        {children}
      </div>
    </>
  );
}

function Note({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <p className="jt-st-note">
      <Icon d={PATHS.shield} size={16} />
      <span>{children}</span>
    </p>
  );
}

function Stepper({
  quantity,
  onChange,
  label,
}: {
  readonly quantity: number;
  readonly onChange: (next: number) => void;
  readonly label: string;
}): JSX.Element {
  return (
    <div className="jt-st-qty">
      <button
        type="button"
        aria-label={`Decrease quantity of ${label}`}
        disabled={quantity <= 1}
        onClick={() => onChange(Math.max(1, quantity - 1))}
      >
        <Icon d={PATHS.minus} size={16} stroke="#17112b" width={2.6} />
      </button>
      <span aria-live="polite">{quantity}</span>
      <button
        type="button"
        aria-label={`Increase quantity of ${label}`}
        disabled={quantity >= 99}
        onClick={() => onChange(Math.min(99, quantity + 1))}
      >
        <Icon d={PATHS.plus} size={16} stroke="#17112b" width={2.6} />
      </button>
    </div>
  );
}

// -- The product sheet -------------------------------------------------------------

const LEGAL_TEXT: Readonly<Record<string, string>> = {
  PHARMACY_MEDICINE: 'Pharmacy medicine (P)',
  PRESCRIPTION_MEDICINE: 'Prescription-only medicine (POM)',
  PHARMACY_QUESTIONNAIRE: 'Sold after a pharmacist’s questions',
};

/**
 * One product, as the frame's sheet draws it: picture, name, price, size and
 * the description the catalogue holds, the legal category the consultation
 * route reports, and the add button.
 */
export function ProductDialog({
  offer,
  storeName,
  inBasket,
  busy,
  onClose,
  onBuy,
  onConsult,
}: {
  readonly offer: OfferCard;
  readonly storeName: string;
  /** The quantity of this listing already in the basket, if any. */
  readonly inBasket: number;
  readonly busy: boolean;
  readonly onClose: () => void;
  /** GSL: add or update; a pharmacy medicine opens its questions instead. */
  readonly onBuy: (quantity: number, requirement: Requirement | undefined) => void;
  readonly onConsult: () => void;
}): JSX.Element {
  const [quantity, setQuantity] = useState(Math.max(1, inBasket));
  const [info, setInfo] = useState(false);
  const detail = useLoad(`offer:${offer.id}`, () => getOffer(offer.id));
  const requirement = useLoad(`req:${offer.id}`, () => getRequirement(offer.id));
  const req = requirement.status === 'loaded' ? requirement.data : undefined;
  const legal = legalOf(offer);
  const pom = legal === 'POM' || req?.reason === 'PRESCRIPTION_MEDICINE';
  const outOfStock = offer.stock?.inStock === false;
  const badge = savingBadge(offer.price);
  const image =
    (detail.status === 'loaded' ? detail.data.images[0]?.url : undefined) ?? offer.image?.url ?? undefined;
  const description =
    detail.status === 'loaded' ? (detail.data.summary ?? detail.data.description) : offer.summary;
  const size = sizeOf(offer);
  const unit = offer.price.resolved ? offer.price.amountMinor : undefined;
  const currency = offer.price.currency ?? 'GBP';

  const legalText =
    req === undefined
      ? undefined
      : req.required
        ? LEGAL_TEXT[req.reason ?? '']
        : 'No pharmacist check needed';
  const note = pom
    ? 'Prescription-only. Answer a few health questions and a prescriber decides whether it is right for you.'
    : req?.required === true
      ? 'Pharmacy medicine. The pharmacist checks your answers to a few questions before it is sent.'
      : undefined;

  let cta: string;
  if (outOfStock) cta = 'Out of stock';
  else if (pom) cta = 'Start consultation';
  else if (unit === undefined) cta = inBasket > 0 ? 'Update basket' : 'Add to basket';
  else cta = `${inBasket > 0 ? 'Update basket · ' : 'Add · '}${money(unit * quantity, currency)}`;

  return (
    <Sheet label={offer.title} onClose={onClose}>
      <div className="jt-st-sheet__scroll">
        <div className="jt-st-pd__media">
          <div
            className="jt-st-pd__img"
            role="img"
            aria-label={offer.image?.altText ?? offer.title}
            style={image !== undefined ? { backgroundImage: `url("${image}")` } : undefined}
          />
          <button type="button" className="jt-st-pd__close" aria-label="Close" onClick={onClose}>
            <Icon d={PATHS.close} size={18} stroke="#17112b" width={2.6} />
          </button>
          {badge !== undefined ? <span className="jt-st-pd__badge">{badge}</span> : null}
        </div>
        <div className="jt-st-pd__body">
          <span className="jt-st-eyebrow">
            {pom
              ? 'PRESCRIPTION ONLY'
              : req?.required === true
                ? 'PHARMACY MEDICINE'
                : (offer.brandName ?? '').toUpperCase()}
          </span>
          <div className="jt-st-pd__titlerow">
            <h2 className="jt-st-pd__title">{offer.title}</h2>
            <button
              type="button"
              className="jt-st-pd__info"
              aria-expanded={info}
              aria-label="Product information"
              onClick={() => setInfo((value) => !value)}
            >
              <Icon d={PATHS.info} size={18} stroke="#55506b" />
            </button>
          </div>
          <span className="jt-st-pd__price">{priceLabel(offer.price)}</span>
          {size !== undefined || description !== undefined ? (
            <span className="jt-st-pd__desc">{[size, description].filter(Boolean).join(' · ')}</span>
          ) : null}
          {info ? (
            <div className="jt-st-pd__facts">
              {legalText !== undefined ? (
                <>
                  <span>Legal category</span>
                  <b>{legalText}</b>
                </>
              ) : null}
              {size !== undefined ? (
                <>
                  <span>Pack</span>
                  <b>{size}</b>
                </>
              ) : null}
              <span>Sold by</span>
              <b>{storeName}</b>
              <span className="jt-st-pd__leaflet">
                Always read the label and patient information leaflet.
              </span>
            </div>
          ) : null}
          {note !== undefined ? <Note>{note}</Note> : null}
          {req !== undefined && req.required && !req.available ? (
            <p className="jt-st-alert" role="alert">
              This pharmacy is not taking orders for this medicine online yet.
            </p>
          ) : null}
        </div>
      </div>
      <div className="jt-st-sheet__foot">
        {!pom && !outOfStock ? (
          <Stepper quantity={quantity} onChange={setQuantity} label={offer.title} />
        ) : null}
        <button
          type="button"
          className="jt-st-cta"
          disabled={outOfStock || busy || (req !== undefined && req.required && !req.available)}
          onClick={() => (pom ? onConsult() : onBuy(quantity, req))}
        >
          {cta}
        </button>
      </div>
    </Sheet>
  );
}

// -- Questions ------------------------------------------------------------------------

type Answers = Readonly<Record<string, AnswerValue | undefined>>;

function isAnswered(question: Question, value: AnswerValue | undefined): boolean {
  if (value === undefined) return false;
  switch (question.kind) {
    case 'ACKNOWLEDGEMENT':
      return value === true;
    case 'TEXT':
    case 'DATE':
    case 'SINGLE':
      return typeof value === 'string' && value.trim().length > 0;
    case 'MULTI':
      return Array.isArray(value) && value.length > 0;
    case 'NUMBER':
      return typeof value === 'number' && Number.isFinite(value);
    case 'MEASUREMENT':
      return typeof value === 'object' && value !== null && 'value' in value && Number.isFinite(value.value);
    case 'GP_PRACTICE':
      return typeof value === 'object' && value !== null && 'name' in value && value.name.trim().length > 0;
    case 'DOCUMENT':
      return (
        typeof value === 'object' && value !== null && 'documentIds' in value && value.documentIds.length > 0
      );
    default:
      return typeof value === 'boolean';
  }
}

function numberProblem(question: Question, value: AnswerValue | undefined): string | undefined {
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'object' && value !== null && 'value' in value
        ? value.value
        : undefined;
  if (n === undefined) return undefined;
  if (question.integer === true && !Number.isInteger(n)) return 'Enter a whole number.';
  if (question.min !== undefined && n < question.min) return `Enter ${String(question.min)} or more.`;
  if (question.max !== undefined && n > question.max) return `Enter ${String(question.max)} or less.`;
  return undefined;
}

/** What still stops the answers going to the pharmacy, or undefined when they can. */
export function questionsProblem(questions: readonly Question[], answers: Answers): string | undefined {
  const shown = questions.filter((q) => isShown(q, questions, (id) => answers[id]));
  const stop = shown.find((q) => q.kind === 'STOP');
  if (stop !== undefined) return stop.prompt;
  for (const question of shown) {
    if (question.kind === 'STOP') continue;
    const value = answers[question.id];
    const needed = question.required || question.kind === 'ACKNOWLEDGEMENT';
    if (needed && !isAnswered(question, value)) return 'Answer every question to continue.';
    const problem = numberProblem(question, value);
    if (problem !== undefined) return problem;
  }
  return undefined;
}

export function answersFor(
  questions: readonly Question[],
  answers: Answers,
): readonly { readonly questionId: string; readonly value: AnswerValue }[] {
  return questions
    .filter((q) => q.kind !== 'STOP' && isShown(q, questions, (id) => answers[id]))
    .flatMap((q) => {
      const value = answers[q.id];
      return value === undefined || !isAnswered(q, value) ? [] : [{ questionId: q.id, value }];
    });
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
}

function QuestionCard({
  question,
  value,
  onChange,
  signedIn,
  tried,
}: {
  readonly question: Question;
  readonly value: AnswerValue | undefined;
  readonly onChange: (value: AnswerValue | undefined) => void;
  readonly signedIn: boolean;
  readonly tried: boolean;
}): JSX.Element {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const missing = tried && question.required && !isAnswered(question, value);
  const problem = numberProblem(question, value);

  if (question.kind === 'STOP') {
    return (
      <div className="jt-st-q jt-st-q--stop" role="alert">
        <span className="jt-st-q__prompt">{question.prompt}</span>
      </div>
    );
  }

  if (question.kind === 'ACKNOWLEDGEMENT') {
    const on = value === true;
    return (
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        className={`jt-st-check${tried && !on ? ' jt-st-check--missing' : ''}`}
        onClick={() => onChange(on ? undefined : true)}
      >
        <span aria-hidden="true" className={`jt-st-check__box${on ? ' jt-st-check__box--on' : ''}`}>
          <Icon d={PATHS.check} size={13} stroke="#fff" width={3.4} />
        </span>
        <span>{question.prompt}</span>
      </button>
    );
  }

  let control: JSX.Element;
  switch (question.kind) {
    case 'YES_NO':
      control = (
        <div role="radiogroup" aria-label={question.prompt} className="jt-st-q__pair">
          {(
            [
              ['Yes', true],
              ['No', false],
            ] as const
          ).map(([label, answer]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={value === answer}
              className={`jt-st-q__opt${value === answer ? ' jt-st-q__opt--on' : ''}`}
              onClick={() => onChange(answer)}
            >
              {label}
            </button>
          ))}
        </div>
      );
      break;
    case 'SINGLE':
      control = (
        <div role="radiogroup" aria-label={question.prompt} className="jt-st-q__list">
          {(question.options ?? []).map((option) => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={value === option.id}
              className={`jt-st-q__opt jt-st-q__opt--row${value === option.id ? ' jt-st-q__opt--on' : ''}`}
              onClick={() => onChange(option.id)}
            >
              <span>{option.label}</span>
              {option.hint !== undefined ? <small>{option.hint}</small> : null}
            </button>
          ))}
        </div>
      );
      break;
    case 'MULTI': {
      const chosen = Array.isArray(value) ? (value as readonly string[]) : [];
      control = (
        <div role="group" aria-label={question.prompt} className="jt-st-q__list">
          {(question.options ?? []).map((option) => {
            const on = chosen.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`jt-st-q__opt jt-st-q__opt--row${on ? ' jt-st-q__opt--on' : ''}`}
                onClick={() => {
                  const next = on ? chosen.filter((id) => id !== option.id) : [...chosen, option.id];
                  onChange(next.length > 0 ? next : undefined);
                }}
              >
                <span>{option.label}</span>
                {option.hint !== undefined ? <small>{option.hint}</small> : null}
              </button>
            );
          })}
        </div>
      );
      break;
    }
    case 'TEXT':
      control = (
        <textarea
          className="jt-st-q__text"
          rows={3}
          aria-label={question.prompt}
          value={typeof value === 'string' ? value : ''}
          maxLength={2000}
          onChange={(event) => onChange(event.target.value.length > 0 ? event.target.value : undefined)}
        />
      );
      break;
    case 'NUMBER':
    case 'MEASUREMENT': {
      const current =
        typeof value === 'number'
          ? value
          : typeof value === 'object' && value !== null && 'value' in value
            ? value.value
            : undefined;
      control = (
        <label className="jt-st-q__num">
          <input
            type="number"
            inputMode="decimal"
            aria-label={question.prompt}
            value={current === undefined ? '' : String(current)}
            {...(question.min !== undefined ? { min: question.min } : {})}
            {...(question.max !== undefined ? { max: question.max } : {})}
            onChange={(event) => {
              const text = event.target.value;
              if (text === '') return onChange(undefined);
              const n = Number(text);
              if (!Number.isFinite(n)) return undefined;
              return onChange(question.kind === 'MEASUREMENT' ? { value: n, unit: question.unit ?? '' } : n);
            }}
          />
          {question.unit !== undefined ? <span>{question.unit}</span> : null}
        </label>
      );
      break;
    }
    case 'DATE':
      control = (
        <input
          type="date"
          className="jt-st-q__date"
          aria-label={question.prompt}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value.length > 0 ? event.target.value : undefined)}
        />
      );
      break;
    case 'GP_PRACTICE':
      control = (
        <input
          type="text"
          className="jt-st-q__date"
          aria-label={question.prompt}
          placeholder="Your GP practice’s name"
          maxLength={200}
          value={typeof value === 'object' && value !== null && 'name' in value ? value.name : ''}
          onChange={(event) =>
            onChange(event.target.value.length > 0 ? { manual: true, name: event.target.value } : undefined)
          }
        />
      );
      break;
    case 'DOCUMENT': {
      const ids =
        typeof value === 'object' && value !== null && 'documentIds' in value ? value.documentIds : [];
      control = (
        <div className="jt-st-q__upload">
          {signedIn ? (
            <label className="jt-st-q__file">
              <input
                type="file"
                accept="image/*,application/pdf"
                disabled={uploading || ids.length >= (question.maxFiles ?? 5)}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file === undefined) return;
                  setUploading(true);
                  setUploadError('');
                  try {
                    const stored = await uploadEvidence(file.name, await readBase64(file));
                    onChange({ documentIds: [...ids, stored.id] });
                  } catch (error) {
                    setUploadError(error instanceof Error ? error.message : 'That file could not be added.');
                  } finally {
                    setUploading(false);
                  }
                }}
              />
              {uploading
                ? 'Adding…'
                : ids.length > 0
                  ? `${String(ids.length)} added · add another`
                  : 'Add a photo or document'}
            </label>
          ) : (
            <span className="jt-st-q__hint">Log in to attach a photo or document.</span>
          )}
          {uploadError !== '' ? <span className="jt-st-q__err">{uploadError}</span> : null}
        </div>
      );
      break;
    }
    default:
      control = <span className="jt-st-q__hint">This question can be answered in the Just Treated app.</span>;
  }

  return (
    <div className={`jt-st-q${missing ? ' jt-st-q--missing' : ''}`}>
      <span className="jt-st-q__prompt">{question.prompt}</span>
      {question.help !== undefined ? <span className="jt-st-q__help">{question.help}</span> : null}
      {control}
      {problem !== undefined ? <span className="jt-st-q__err">{problem}</span> : null}
    </div>
  );
}

function QuestionList({
  questions,
  answers,
  setAnswer,
  signedIn,
  tried,
}: {
  readonly questions: readonly Question[];
  readonly answers: Answers;
  readonly setAnswer: (id: string, value: AnswerValue | undefined) => void;
  readonly signedIn: boolean;
  readonly tried: boolean;
}): JSX.Element {
  const shown = questions.filter((q) => isShown(q, questions, (id) => answers[id]));
  return (
    <>
      {shown.map((question) => (
        <QuestionCard
          key={question.id}
          question={question}
          value={answers[question.id]}
          onChange={(value) => setAnswer(question.id, value)}
          signedIn={signedIn}
          tried={tried}
        />
      ))}
    </>
  );
}

function useAnswers(): readonly [Answers, (id: string, value: AnswerValue | undefined) => void] {
  const [answers, setAnswers] = useState<Answers>({});
  const set = (id: string, value: AnswerValue | undefined): void =>
    setAnswers((previous) => ({ ...previous, [id]: value }));
  return [answers, set] as const;
}

function refusalText(error: unknown): string {
  return error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';
}

/**
 * A pharmacy medicine — the frame's "A few quick questions".
 *
 * The questions are the pharmacy's own published questionnaire. Sending the
 * answers needs an account (the platform keeps them against a person), so a
 * visitor who is not logged in answers here and is offered Log in, which
 * brings them straight back.
 */
export function PharmacyQuestions({
  offer,
  storeName,
  quantity,
  signedIn,
  onClose,
  onAnswered,
}: {
  readonly offer: OfferCard;
  readonly storeName: string;
  readonly quantity: number;
  readonly signedIn: boolean;
  readonly onClose: () => void;
  /** Called with the consultation the answers became; the page adds the line with it. */
  readonly onAnswered: (consultationId: string) => Promise<boolean>;
}): JSX.Element {
  const requirement = useLoad(`req:${offer.id}`, () => getRequirement(offer.id));
  const [answers, setAnswer] = useAnswers();
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const req = requirement.status === 'loaded' ? requirement.data : undefined;
  const questions = req?.questionnaire?.questions ?? [];
  const problem = questionsProblem(questions, answers);
  const unit = offer.price.resolved ? offer.price.amountMinor : undefined;
  const total = unit === undefined ? '' : ` · ${money(unit * quantity, offer.price.currency ?? 'GBP')}`;

  const go = async (): Promise<void> => {
    setTried(true);
    if (problem !== undefined || req === undefined) return;
    setSending(true);
    setError('');
    try {
      const consultation = await submitConsultation(offer.id, answersFor(questions, answers));
      if (await onAnswered(consultation.id)) onClose();
    } catch (failure) {
      setError(refusalText(failure));
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet labelledBy="jt-st-pq-title" onClose={onClose} layer={2}>
      <div className="jt-st-pq__head">
        <span aria-hidden="true" className="jt-st-pq__icon">
          <Icon d={PATHS.shield} size={20} />
        </span>
        <div className="jt-st-pq__titles">
          <span id="jt-st-pq-title" className="jt-st-pq__title">
            A few quick questions
          </span>
          <span className="jt-st-pq__sub">
            {offer.title} is a pharmacy medicine. These questions are set by {storeName}, and their pharmacist
            checks your answers before it&rsquo;s sent.
          </span>
        </div>
        <button type="button" className="jt-st-round" aria-label="Close" onClick={onClose}>
          <Icon d={PATHS.close} size={18} stroke="#17112b" width={2.6} />
        </button>
      </div>
      <div className="jt-st-sheet__scroll jt-st-pq__body">
        {requirement.status === 'loading' ? (
          <p className="jt-st-muted">Loading the pharmacy’s questions…</p>
        ) : null}
        {requirement.status === 'error' ? (
          <p className="jt-st-alert" role="alert">
            {requirement.message}
          </p>
        ) : null}
        {req !== undefined && !req.available ? (
          <p className="jt-st-alert" role="alert">
            This pharmacy is not taking orders for this medicine online yet.
          </p>
        ) : null}
        {req?.questionnaire?.introduction !== undefined ? (
          <p className="jt-st-muted">{req.questionnaire.introduction}</p>
        ) : null}
        <QuestionList
          questions={questions}
          answers={answers}
          setAnswer={setAnswer}
          signedIn={signedIn}
          tried={tried}
        />
        {tried && problem !== undefined ? (
          <span role="alert" className="jt-st-q__err">
            {problem}
          </span>
        ) : null}
        {error !== '' ? (
          <span role="alert" className="jt-st-q__err">
            {error}
          </span>
        ) : null}
        {!signedIn && req?.available === true ? (
          <AccountNote verb="send your answers" storeName={storeName} />
        ) : null}
      </div>
      <div className="jt-st-sheet__foot">
        {signedIn ? (
          <button
            type="button"
            className="jt-st-cta"
            disabled={sending || req === undefined || !req.available}
            onClick={() => void go()}
          >
            {sending ? 'Sending…' : `Add to basket${total}`}
          </button>
        ) : (
          <LoginLink className="jt-st-cta jt-st-cta--link">Log in to continue</LoginLink>
        )}
      </div>
    </Sheet>
  );
}

function AccountNote({
  verb,
  storeName,
}: {
  readonly verb: string;
  readonly storeName: string;
}): JSX.Element {
  return (
    <div role="note" className="jt-st-acct">
      <span aria-hidden="true" className="jt-st-acct__icon">
        <Icon d={PATHS.person} size={16} stroke="#5c3206" width={2.4} />
      </span>
      <span className="jt-st-acct__text">
        <b>Log in to {verb}</b>
        <span>
          {storeName} needs an account to keep your answers and records safe. We&rsquo;ll bring you straight
          back here.
        </span>
      </span>
    </div>
  );
}

// -- The flow dialog (consultation and booking share the frame's shell) ---------------

function FlowHead({
  title,
  sub,
  steps,
  step,
  onBack,
  onClose,
}: {
  readonly title: string;
  readonly sub: string;
  readonly steps: readonly string[];
  readonly step: number;
  readonly onBack?: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  return (
    <div className="jt-st-flow__head">
      <div className="jt-st-flow__row">
        {onBack !== undefined ? (
          <button type="button" className="jt-st-round jt-st-round--lg" aria-label="Back" onClick={onBack}>
            <Icon d={PATHS.back} size={16} stroke="#17112b" width={2.6} />
          </button>
        ) : null}
        <div className="jt-st-flow__titles">
          <span className="jt-st-flow__title">{title}</span>
          <span className="jt-st-flow__sub">{sub}</span>
        </div>
        <button type="button" className="jt-st-round jt-st-round--lg" aria-label="Close" onClick={onClose}>
          <Icon d={PATHS.close} size={16} stroke="#17112b" width={2.6} />
        </button>
      </div>
      {step < steps.length ? (
        <div className="jt-st-flow__prog">
          <div
            className="jt-st-flow__bars"
            style={{ gridTemplateColumns: `repeat(${String(steps.length)},1fr)` }}
          >
            {steps.map((label, index) => (
              <span
                key={label}
                className={index <= step ? 'jt-st-flow__bar jt-st-flow__bar--on' : 'jt-st-flow__bar'}
              />
            ))}
          </div>
          <span className="jt-st-flow__step">{steps[step]}</span>
        </div>
      ) : null}
    </div>
  );
}

function Done({
  title,
  text,
  reference,
  children,
}: {
  readonly title: string;
  readonly text: string;
  readonly reference?: string;
  readonly children?: ReactNode;
}): JSX.Element {
  return (
    <div className="jt-st-done">
      <span className="jt-st-done__tick">
        <Icon d={PATHS.check} size={34} stroke="#156b49" width={2.6} />
      </span>
      <span className="jt-st-done__title">{title}</span>
      <span className="jt-st-done__text">{text}</span>
      {reference !== undefined ? <span className="jt-st-done__ref">REFERENCE {reference}</span> : null}
      {children}
    </div>
  );
}

/**
 * "View in my bookings" — to the booking itself on the Bookings page (stage 8).
 * Inert, saying so, while that page is not available.
 */
function BookingsLink({ bookingId }: { readonly bookingId: string }): JSX.Element {
  if (routes.bookings.availability !== 'available') {
    return (
      <span className="jt-st-done__link" aria-disabled="true">
        View in my bookings <span className="jt-st-soon">Coming soon</span>
      </span>
    );
  }
  return (
    <AppLink
      to={`${routes.bookings.path}/${encodeURIComponent(bookingId)}`}
      className="jt-st-done__link jt-st-done__link--live"
    >
      View in my bookings
    </AppLink>
  );
}

/**
 * A prescription-only medicine — never added straight to the basket.
 *
 * The frame's consultation flow: the pharmacy's health questions, then check
 * and confirm. Submitting needs an account; the consultation the answers
 * become is what lets the medicine into the basket at all, and the prescriber
 * reviews it after the order is paid (the platform's order, owner decision
 * D9) — so the page says that, not the frame's "won't be charged unless
 * approved".
 */
export function Consultation({
  offer,
  storeName,
  signedIn,
  onClose,
  onSubmitted,
}: {
  readonly offer: OfferCard;
  readonly storeName: string;
  readonly signedIn: boolean;
  readonly onClose: () => void;
  readonly onSubmitted: (consultationId: string) => Promise<boolean>;
}): JSX.Element {
  const requirement = useLoad(`req:${offer.id}`, () => getRequirement(offer.id));
  const [answers, setAnswer] = useAnswers();
  const [step, setStep] = useState(0);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reference, setReference] = useState<string | undefined>(undefined);
  const req = requirement.status === 'loaded' ? requirement.data : undefined;
  const questions = req?.questionnaire?.questions ?? [];
  const problem = questionsProblem(questions, answers);
  const steps = ['HEALTH QUESTIONS', 'CONFIRM'];
  const price = priceLabel(offer.price);

  const next = async (): Promise<void> => {
    if (step === 0) {
      setTried(true);
      if (problem === undefined && req?.available === true) setStep(1);
      return;
    }
    if (step === 1 && signedIn) {
      setSending(true);
      setError('');
      try {
        const consultation = await submitConsultation(offer.id, answersFor(questions, answers));
        if (await onSubmitted(consultation.id)) {
          setReference(consultation.reference ?? consultation.id);
          setStep(2);
        }
      } catch (failure) {
        setError(refusalText(failure));
      } finally {
        setSending(false);
      }
    }
  };

  return (
    <Sheet label={offer.title} onClose={onClose} wide layer={1}>
      <FlowHead
        title={offer.title}
        sub={`${storeName} · ${price}`}
        steps={steps}
        step={step}
        {...(step === 1 ? { onBack: () => setStep(0) } : {})}
        onClose={onClose}
      />
      <div className="jt-st-sheet__scroll jt-st-flow__body">
        {step === 0 ? (
          <>
            <div className="jt-st-flow__intro">
              <span>A few health questions</span>
              <span>Set by {storeName}. Your answers go to their prescriber and are kept confidential.</span>
            </div>
            {requirement.status === 'loading' ? <p className="jt-st-muted">Loading the questions…</p> : null}
            {requirement.status === 'error' ? (
              <p className="jt-st-alert" role="alert">
                {requirement.message}
              </p>
            ) : null}
            {req !== undefined && !req.available ? (
              <p className="jt-st-alert" role="alert">
                This pharmacy is not taking consultations for this medicine online yet.
              </p>
            ) : null}
            <QuestionList
              questions={questions}
              answers={answers}
              setAnswer={setAnswer}
              signedIn={signedIn}
              tried={tried}
            />
            {tried && problem !== undefined ? (
              <span role="alert" className="jt-st-q__err">
                {problem}
              </span>
            ) : null}
          </>
        ) : null}
        {step === 1 ? (
          <>
            <span className="jt-st-flow__h">Check and confirm</span>
            <div className="jt-st-review">
              <div>
                <span>Treatment</span>
                <b>{offer.title}</b>
              </div>
              <div>
                <span>Reviewed by</span>
                <b>A prescriber at {storeName}</b>
              </div>
              <div>
                <span>Price</span>
                <b>{price}</b>
              </div>
            </div>
            <Note>
              It goes in your basket now. You pay at checkout, then a prescriber reviews your answers before
              anything is sent. If they decline, you are refunded in full.
            </Note>
            {!signedIn ? <AccountNote verb="submit your consultation" storeName={storeName} /> : null}
            {error !== '' ? (
              <span role="alert" className="jt-st-q__err">
                {error}
              </span>
            ) : null}
          </>
        ) : null}
        {step === 2 ? (
          <Done
            title="Consultation submitted"
            text={`${offer.title} is in your basket. A prescriber reviews your answers after you pay.`}
            {...(reference !== undefined ? { reference } : {})}
          />
        ) : null}
      </div>
      <div className="jt-st-flow__foot">
        {step === 1 && !signedIn ? (
          <LoginLink className="jt-st-cta jt-st-cta--link">Log in to continue</LoginLink>
        ) : (
          <button
            type="button"
            className="jt-st-cta"
            disabled={sending || req === undefined || !req.available}
            onClick={() => (step === 2 ? onClose() : void next())}
          >
            {step === 2 ? 'Done' : step === 1 ? (sending ? 'Sending…' : 'Submit for review') : 'Continue'}
          </button>
        )}
      </div>
    </Sheet>
  );
}

// -- Book a service ------------------------------------------------------------------

const WINDOW_DAYS = 60;
const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function download(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * "Book a service" — the pharmacy's real diary.
 *
 * Dates with a dot are days the diary has room on; a time is held and then
 * confirmed only when somebody logged in presses Confirm. A new appointment
 * comes back PENDING, so the page says "requested" and that the pharmacy
 * still has to accept — never "booked" for a slot nobody accepted.
 */
export function BookService({
  service,
  storeName,
  where,
  signedIn,
  onClose,
  onToast,
}: {
  readonly service: OfferCard;
  readonly storeName: string;
  readonly where: string;
  readonly signedIn: boolean;
  readonly onClose: () => void;
  readonly onToast: (message: string) => void;
}): JSX.Element {
  const today = useMemo(() => new Date(), []);
  const diary = useLoad(`slots:${service.id}`, () =>
    getSlots(service.id, today, new Date(today.getTime() + WINDOW_DAYS * 86_400_000)),
  );
  const availability: readonly Availability[] = diary.status === 'loaded' ? diary.data : [];
  const modes = useMemo(() => {
    const all = new Set(availability.flatMap((a) => a.slots.flatMap((s) => s.modes)));
    return ['IN_PERSON', 'VIRTUAL'].filter((m) => all.has(m));
  }, [availability]);
  const [mode, setMode] = useState<string | undefined>(undefined);
  const activeMode = mode ?? modes[0];
  const zone = availability[0]?.timeZone ?? 'Europe/London';

  const open = useMemo(() => {
    const byDay = new Map<string, { slot: Slot; availabilityId: string }[]>();
    for (const rule of availability) {
      for (const slot of rule.slots) {
        if (slot.remaining <= 0) continue;
        if (activeMode !== undefined && !slot.modes.includes(activeMode)) continue;
        if (new Date(slot.startsAt).getTime() <= Date.now()) continue;
        const key = dayKey(slot.startsAt, slot.timeZone);
        byDay.set(key, [...(byDay.get(key) ?? []), { slot, availabilityId: rule.availabilityId }]);
      }
    }
    for (const list of byDay.values()) list.sort((a, b) => a.slot.startsAt.localeCompare(b.slot.startsAt));
    return byDay;
  }, [availability, activeMode]);

  const first = [...open.keys()].sort()[0];
  const [month, setMonth] = useState<{ y: number; m: number }>(() => ({
    y: today.getFullYear(),
    m: today.getMonth(),
  }));
  const [day, setDay] = useState<string | undefined>(undefined);
  const [pick, setPick] = useState<{ slot: Slot; availabilityId: string } | undefined>(undefined);
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState<Booking | undefined>(undefined);
  const holdRef = useRef<string | undefined>(undefined);

  useEffect(
    () => () => {
      // Walked away between hold and confirm: give the time back.
      if (holdRef.current !== undefined) void releaseHold(holdRef.current);
    },
    [],
  );

  useEffect(() => {
    if (first === undefined) return;
    const [y, m] = first.split('-').map(Number);
    if (y !== undefined && m !== undefined) setMonth({ y, m: m - 1 });
  }, [first]);

  const todayKey = dayKey(today.toISOString(), zone);
  const lastKey = dayKey(new Date(today.getTime() + WINDOW_DAYS * 86_400_000).toISOString(), zone);
  const cells = monthGrid(month.y, month.m);
  const monthTitle = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(month.y, month.m, 1)));
  const canPrev =
    month.y > today.getFullYear() || (month.y === today.getFullYear() && month.m > today.getMonth());
  const lastMonth = new Date(today.getTime() + WINDOW_DAYS * 86_400_000);
  const canNext =
    month.y < lastMonth.getFullYear() ||
    (month.y === lastMonth.getFullYear() && month.m < lastMonth.getMonth());
  const daySlots = day === undefined ? [] : (open.get(day) ?? []);
  const groups = slotGroups(daySlots.map((entry) => entry.slot));
  const price = priceLabel(service.price);
  const minutes = service.service?.typicalMinutes;
  const when = pick === undefined ? undefined : whenLabel(pick.slot.startsAt, pick.slot.timeZone);

  const confirm = async (): Promise<void> => {
    if (pick === undefined) return;
    setSending(true);
    setError('');
    try {
      const hold = await holdSlot({
        listingId: service.id,
        availabilityId: pick.availabilityId,
        startsAt: pick.slot.startsAt,
        mode: activeMode ?? pick.slot.modes[0] ?? 'IN_PERSON',
        idempotencyKey: newKey(),
      });
      holdRef.current = hold.id;
      const made = await confirmBooking(hold.id, newKey());
      holdRef.current = undefined;
      setBooking(made);
      setStep(2);
    } catch (failure) {
      setError(refusalText(failure));
      if (holdRef.current !== undefined) {
        void releaseHold(holdRef.current);
        holdRef.current = undefined;
      }
    } finally {
      setSending(false);
    }
  };

  const confirmed = booking?.status === 'CONFIRMED';

  return (
    <Sheet label={service.title} onClose={onClose} wide layer={1}>
      <FlowHead
        title={service.title}
        sub={`${storeName} · ${price}${minutes !== undefined ? ` · ${String(minutes)} min` : ''}`}
        steps={['DATE & TIME', 'CONFIRM']}
        step={step}
        {...(step === 1 ? { onBack: () => setStep(0) } : {})}
        onClose={onClose}
      />
      <div className="jt-st-sheet__scroll jt-st-flow__body">
        {step === 0 ? (
          <>
            {modes.length > 1 ? (
              <div role="tablist" aria-label="Appointment type" className="jt-st-seg">
                {modes.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={activeMode === m}
                    className={activeMode === m ? 'jt-st-seg__on' : ''}
                    onClick={() => {
                      setMode(m);
                      setDay(undefined);
                      setPick(undefined);
                    }}
                  >
                    {m === 'VIRTUAL' ? 'Video call' : 'In pharmacy'}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="jt-st-cal__head">
              <span>{monthTitle}</span>
              <span className="jt-st-cal__nav">
                <button
                  type="button"
                  aria-label="Previous month"
                  disabled={!canPrev}
                  onClick={() => setMonth(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}
                >
                  <Icon d={PATHS.chevronLeft} size={14} width={2.8} />
                </button>
                <button
                  type="button"
                  aria-label="Next month"
                  disabled={!canNext}
                  onClick={() => setMonth(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}
                >
                  <Icon d={PATHS.chevronRight} size={14} width={2.8} />
                </button>
              </span>
            </div>
            <div className="jt-st-cal">
              {DOW.map((d) => (
                <span key={d} className="jt-st-cal__dow">
                  {d}
                </span>
              ))}
              {cells.map((cell, index) => {
                if (cell === undefined) return <span key={`b${String(index)}`} />;
                const has = open.has(cell);
                const off = !has || cell < todayKey || cell > lastKey;
                const on = cell === day;
                const label = Number(cell.slice(8));
                return (
                  <button
                    key={cell}
                    type="button"
                    disabled={off}
                    aria-pressed={on}
                    aria-label={`${new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${cell}T12:00:00Z`))}${has ? ', times available' : ''}`}
                    className={`jt-st-cal__day${on ? ' jt-st-cal__day--on' : ''}${off ? ' jt-st-cal__day--off' : ''}`}
                    onClick={() => {
                      setDay(cell);
                      setPick(undefined);
                    }}
                  >
                    {label}
                    <span className={has && !off ? 'jt-st-cal__dot jt-st-cal__dot--on' : 'jt-st-cal__dot'} />
                  </button>
                );
              })}
            </div>
            {diary.status === 'loading' ? <p className="jt-st-muted">Checking the diary…</p> : null}
            {diary.status === 'error' ? (
              <p className="jt-st-alert" role="alert">
                {diary.message}
              </p>
            ) : null}
            {diary.status === 'loaded' && open.size === 0 ? (
              <p className="jt-st-hintbox">
                {storeName} has no times free for this in the next {WINDOW_DAYS} days.
              </p>
            ) : null}
            {day !== undefined && groups.length > 0 ? (
              <div className="jt-st-slots">
                <div className="jt-st-slots__head">
                  <span>
                    {new Intl.DateTimeFormat('en-GB', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                      timeZone: 'UTC',
                    }).format(new Date(`${day}T12:00:00Z`))}
                  </span>
                  <span>
                    {daySlots.length} {daySlots.length === 1 ? 'time' : 'times'}
                  </span>
                </div>
                {groups.map((group) => (
                  <div key={group.label} className="jt-st-slots__group">
                    <span className="jt-st-slots__label">{group.label}</span>
                    <div className="jt-st-slots__grid">
                      {group.slots.map((slot) => {
                        const entry = daySlots.find((candidate) => candidate.slot === slot);
                        const on = pick?.slot === slot;
                        return (
                          <button
                            key={slot.startsAt}
                            type="button"
                            aria-pressed={on}
                            className={on ? 'jt-st-slot jt-st-slot--on' : 'jt-st-slot'}
                            onClick={() => setPick(entry)}
                          >
                            {timeOf(slot.startsAt, slot.timeZone)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : diary.status === 'loaded' && open.size > 0 ? (
              <p className="jt-st-hintbox">Pick a day with a dot to see available times.</p>
            ) : null}
          </>
        ) : null}
        {step === 1 && pick !== undefined ? (
          <>
            <span className="jt-st-flow__h">Check and confirm</span>
            <div className="jt-st-review">
              <div>
                <span>Service</span>
                <b>{service.title}</b>
              </div>
              <div>
                <span>Where</span>
                <b>{activeMode === 'VIRTUAL' ? 'Video call' : where}</b>
              </div>
              <div>
                <span>When</span>
                <b>{when}</b>
              </div>
              <div>
                <span>Price</span>
                <b>{price}</b>
              </div>
            </div>
            <Note>{storeName} confirms your appointment. Until they do, it shows as requested.</Note>
            {!signedIn ? <AccountNote verb="confirm your booking" storeName={storeName} /> : null}
            {error !== '' ? (
              <span role="alert" className="jt-st-q__err">
                {error}
              </span>
            ) : null}
          </>
        ) : null}
        {step === 2 && booking !== undefined ? (
          <Done
            title={confirmed ? 'You’re booked in' : 'Booking requested'}
            text={`${service.title} · ${whenLabel(booking.startsAt, booking.timeZone)}. ${
              confirmed ? `${storeName} has confirmed it.` : `Waiting for ${storeName} to accept.`
            }`}
            reference={booking.id}
          >
            <BookingsLink bookingId={booking.id} />
          </Done>
        ) : null}
      </div>
      <div className="jt-st-flow__foot">
        {step === 0 && when !== undefined ? (
          <div className="jt-st-flow__sel">
            <span>Selected</span>
            <b>
              {when}
              {activeMode === 'VIRTUAL' ? ' · Video' : ''}
            </b>
          </div>
        ) : null}
        <div className="jt-st-flow__btns">
          {step === 2 && booking !== undefined ? (
            <button
              type="button"
              className="jt-st-cta jt-st-cta--ghost"
              onClick={() => {
                download(
                  'just-treated-appointment.ics',
                  bookingIcs({
                    id: booking.id,
                    title: `${service.title} at ${storeName}`,
                    where: activeMode === 'VIRTUAL' ? 'Video call' : where,
                    startsAt: booking.startsAt,
                    endsAt: booking.endsAt,
                  }),
                  'text/calendar',
                );
                onToast('Added to your calendar');
              }}
            >
              Add to calendar
            </button>
          ) : null}
          {step === 1 && !signedIn ? (
            <LoginLink className="jt-st-cta jt-st-cta--link">Log in to continue</LoginLink>
          ) : (
            <button
              type="button"
              className="jt-st-cta"
              disabled={(step === 0 && pick === undefined) || sending}
              onClick={() => {
                if (step === 0) setStep(1);
                else if (step === 1) void confirm();
                else onClose();
              }}
            >
              {step === 2 ? 'Done' : step === 1 ? (sending ? 'Booking…' : 'Confirm booking') : 'Continue'}
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}
